import { html } from 'lit';
import { measureCallLatency } from '../call-latency.js';

export interface CallDiagnosticLog {
  timestamp?: Date | string | number | null;
  type?: string | null;
  payload?: string | null;
}
export const diagnosticEventTypes = new Set([
  'llm-request',
  'llm-attempt',
  'llm-response',
  'llm-error',
  'llm-recovery',
  'tool-execution',
  'crm-request-start',
  'crm-request-end',
  'crm-contact-response',
  'crm-contact-decision',
  'crm-route-decision',
  'authentication-check',
]);
export const isDiagnosticLog = (log: CallDiagnosticLog) => diagnosticEventTypes.has(String(log.type));
export function diagnosticPayload(log: CallDiagnosticLog): Record<string, any> {
  try {
    return JSON.parse(log.payload ?? '{}');
  } catch {
    return { raw: log.payload };
  }
}
export function diagnosticSummary(log: CallDiagnosticLog) {
  const data = diagnosticPayload(log);
  if (log.type === 'authentication-check') {
    const view = authenticationDiagnosticView(data);
    return `Authentication · ${view.field} · ${view.decision.outcome}${view.truncated ? ' · truncated' : ''}`;
  }
  if (log.type === 'crm-request-start') return `CRM: ${data.toolName ?? '?'} · gestartet`;
  if (log.type === 'crm-request-end')
    return `CRM: ${data.toolName ?? '?'} · ${data.outcome ?? '?'} · ${data.resultCount ?? '—'} Treffer${data.code ? ` · ${data.code}` : ''}`;
  if (log.type === 'crm-contact-response')
    return `CRM-Kontaktantwort: ${data.toolName ?? '?'} · ${data.resultCount ?? '—'} Kontakte${data.truncated ? ' · gekürzt' : ''}`;
  if (log.type === 'crm-contact-decision')
    return `Kontaktsuche: ${data.outcome ?? '?'} · ${data.resultCount ?? '—'} Kontakte`;
  if (log.type === 'crm-route-decision')
    return `Kontaktzuordnung: ${data.routeFamily ?? data.route ?? '?'} · ${data.matchingContactCount ?? 0}/${data.accountContactCount ?? 0} passend · ${data.reason ?? '?'}`;
  if (log.type === 'llm-request')
    return `Verfügbare Tools: ${(data.availableTools ?? []).join(', ') || 'keine'} · ${data.provider ?? '?'} / ${data.model ?? '?'}`;
  if (log.type === 'tool-execution') return `Tool: ${data.tool ?? '?'} · ${data.outcome ?? '?'}`;
  if (log.type === 'llm-error')
    return `Fehler: ${data.category ?? 'unbekannt'}${data.status ? ` (HTTP ${data.status})` : ''}`;
  if (log.type === 'llm-recovery') return `Maßnahme: ${data.action ?? '?'} · ${data.outcome ?? '?'}`;
  if (log.type === 'llm-response')
    return `Modellantwort: ${data.outcome ?? '?'}${data.selectedTools?.length ? ` · ${data.selectedTools.map((t: any) => t.name).join(', ')}` : ''}`;
  return `Versuch ${data.attempt ?? '?'} · ${data.outcome ?? '?'}`;
}
export const formatDiagnosticAsText = (log: CallDiagnosticLog) => `[${log.type}] ${log.payload ?? '{}'}`;

const diagnosticObject = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const diagnosticText = (value: unknown, limit = 512): string =>
  typeof value === 'string' ? value.slice(0, limit) : '';
const diagnosticCount = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;

/** Project bounded, optional diagnostic data. Missing scores are never displayed as zero or success. */
export function authenticationDiagnosticView(value: unknown) {
  const data = diagnosticObject(value);
  const match = (value: unknown) => {
    const item = diagnosticObject(value);
    const score = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1 ? v : undefined);
    return {
      score: score(item.score),
      rawScore: score(item.rawScore),
      threshold: score(item.threshold),
      accepted: typeof item.accepted === 'boolean' ? item.accepted : undefined,
      phoneticBoost: item.phoneticBoost === true,
      method: diagnosticText(item.method, 40) || 'not recorded',
    };
  };
  const source = Array.isArray(data.candidates) ? data.candidates : [];
  const candidates = source.slice(0, 25).map(value => {
    const row = diagnosticObject(value);
    return {
      id: diagnosticText(row.id, 128),
      firstName: diagnosticText(row.firstName, 128),
      lastName: diagnosticText(row.lastName, 128),
      email: diagnosticText(row.email, 320),
      phoneMatch: typeof row.phoneMatch === 'boolean' ? row.phoneMatch : undefined,
      eligible: typeof row.eligible === 'boolean' ? row.eligible : undefined,
      firstNameMatch: row.firstNameMatch ? match(row.firstNameMatch) : undefined,
      surnameMatch: row.surnameMatch ? match(row.surnameMatch) : undefined,
      emailMatch: row.emailMatch ? match(row.emailMatch) : undefined,
      namePairs: (Array.isArray(row.namePairs) ? row.namePairs : []).slice(0, 2).map((value: unknown) => {
        const pair = diagnosticObject(value);
        return {
          firstName: diagnosticText(pair.firstName, 128),
          lastName: diagnosticText(pair.lastName, 128),
          firstNameMatch: match(pair.firstNameMatch),
          surnameMatch: match(pair.surnameMatch),
          accepted: typeof pair.accepted === 'boolean' ? pair.accepted : undefined,
        };
      }),
    };
  });
  const decision = diagnosticObject(data.decision);
  const judge = diagnosticObject(data.judge);
  const fuzzy = diagnosticObject(data.fuzzy);
  const scope = diagnosticObject(data.scope);
  return {
    field: diagnosticText(data.field, 40) || 'not recorded',
    submitted: diagnosticText(data.submitted),
    callerText: diagnosticText(data.callerText, 1000),
    scope: [scope.intent, scope.route, scope.referenceKind]
      .map(v => diagnosticText(v, 60))
      .filter(Boolean)
      .join(' · '),
    candidates,
    candidateCount: diagnosticCount(data.candidateCount),
    omittedCount: (diagnosticCount(data.omittedCount) ?? 0) + Math.max(0, source.length - candidates.length),
    truncated: data.truncated === true || source.length > candidates.length,
    failedChecks: diagnosticCount(data.failedChecks),
    attemptLimit: diagnosticCount(data.attemptLimit),
    elapsedMs: diagnosticCount(data.elapsedMs),
    decision: {
      outcome: diagnosticText(decision.outcome, 80) || 'not recorded',
      reason: diagnosticText(decision.reason),
      nextPhase: diagnosticText(decision.nextPhase, 80),
      selectedCandidateId: diagnosticText(decision.selectedCandidateId, 128),
    },
    fuzzy: {
      matched: typeof fuzzy.matched === 'boolean' ? fuzzy.matched : undefined,
      ambiguous: typeof fuzzy.ambiguous === 'boolean' ? fuzzy.ambiguous : undefined,
      selectedCandidateId: diagnosticText(fuzzy.selectedCandidateId, 128),
    },
    judge: {
      status: diagnosticText(judge.status, 80),
      decision: diagnosticText(judge.decision, 40),
      candidateId: diagnosticText(judge.candidateId, 128),
      unclearPart: diagnosticText(judge.unclearPart, 40),
      reason: diagnosticText(judge.reason, 400),
    },
  };
}

const authPercentage = (value: number | undefined) =>
  value === undefined ? 'not recorded' : `${(value * 100).toFixed(2)}%`;
const authCheck = (value: boolean | undefined) => (value === undefined ? 'not recorded' : value ? 'yes' : 'no');
const renderAuthMatch = (
  label: string,
  value: ReturnType<typeof authenticationDiagnosticView>['candidates'][number]['emailMatch'],
) =>
  value
    ? html`<div class="mt-1">
        <strong>${label}:</strong> ${value.method} · similarity ${authPercentage(value.score)} · threshold
        ${authPercentage(value.threshold)} · accepted: ${authCheck(value.accepted)}
        ${value.phoneticBoost
          ? html`<span> · phonetic rule applied · raw similarity ${authPercentage(value.rawScore)}</span>`
          : ''}
      </div>`
    : '';

const renderAuthenticationDiagnostic = (data: unknown, summary: string) => {
  const view = authenticationDiagnosticView(data);
  return html`<details class="rounded-lg border border-info/25 bg-info/5 p-3 my-1 text-sm">
    <summary class="cursor-pointer wrap-break-word font-semibold">${summary}</summary>
    <div class="mt-3 space-y-3 wrap-break-word" style="overflow-wrap:anywhere">
      <div>
        <strong>Authentication details</strong>
        ${view.scope ? html`<div class="text-xs text-base-content/65">${view.scope}</div>` : ''}
        <div class="mt-1">Submitted to verification: <code>${view.submitted || 'not recorded'}</code></div>
        ${view.callerText
          ? html`<details class="mt-1">
              <summary class="cursor-pointer text-xs">Caller transcript</summary>
              <p>${view.callerText}</p>
            </details>`
          : ''}
        ${view.failedChecks !== undefined
          ? html`<div class="mt-1">
              Counted failed checks:
              ${view.failedChecks}${view.attemptLimit === undefined ? '' : ` / ${view.attemptLimit}`}
            </div>`
          : ''}
      </div>
      <section aria-label="Authentication candidates">
        <strong
          >Candidate contacts
          (${view.candidates.length}${view.candidateCount === undefined ? '' : ` / ${view.candidateCount}`}
          shown)</strong
        >
        <p class="mt-1 text-xs text-base-content/65">
          Scores measure text similarity, not the probability of a correct identity. Phone evidence and candidate
          eligibility also apply.
        </p>
        <div class="mt-2 space-y-2 max-h-96 overflow-y-auto">
          ${view.candidates.map(
            row =>
              html`<div class="rounded border border-base-300 bg-base-100 p-2">
                <div class="font-semibold">
                  ${[row.firstName, row.lastName].filter(Boolean).join(' ') || 'Unnamed contact'}
                </div>
                ${row.id ? html`<div class="text-xs">CRM contact: <code>${row.id}</code></div>` : ''}
                ${row.email ? html`<div>Email: <code>${row.email}</code></div>` : ''}
                ${row.phoneMatch === undefined ? '' : html`<div>Phone candidate: ${authCheck(row.phoneMatch)}</div>`}
                ${row.eligible === undefined
                  ? ''
                  : html`<div>Eligible for this check: ${authCheck(row.eligible)}</div>`}
                ${renderAuthMatch('First name', row.firstNameMatch)} ${renderAuthMatch('Surname', row.surnameMatch)}
                ${renderAuthMatch('Email', row.emailMatch)}
                ${row.namePairs.map(
                  pair =>
                    html`<div class="mt-2 border-t border-base-300 pt-1">
                      <div>
                        Name pairing: ${pair.firstName} / ${pair.lastName} · accepted: ${authCheck(pair.accepted)}
                      </div>
                      ${renderAuthMatch('First name', pair.firstNameMatch)}${renderAuthMatch(
                        'Surname',
                        pair.surnameMatch,
                      )}
                    </div>`,
                )}
              </div>`,
          )}
        </div>
        ${!view.candidates.length ? html`<p class="mt-1">No candidate rows recorded for this event.</p>` : ''}
        ${view.truncated || view.omittedCount
          ? html`<p class="mt-2 font-medium">
              Diagnostic details were
              limited${view.omittedCount ? `; ${view.omittedCount} candidate rows omitted` : ''}. Verification used the
              full eligible data.
            </p>`
          : ''}
      </section>
      ${view.field === 'email'
        ? html`<section class="border-t border-base-300 pt-2">
            <strong>Fuzzy matching and AI verifier</strong>
            <div>Fuzzy match: ${authCheck(view.fuzzy.matched)} · ambiguous: ${authCheck(view.fuzzy.ambiguous)}</div>
            ${view.fuzzy.selectedCandidateId
              ? html`<div>Fuzzy contact: <code>${view.fuzzy.selectedCandidateId}</code></div>`
              : ''}
            <div class="mt-1">
              AI verifier status:
              ${view.judge.status || 'not recorded'}${view.judge.decision ? ` · decision: ${view.judge.decision}` : ''}
            </div>
            ${view.judge.candidateId ? html`<div>Verifier contact: <code>${view.judge.candidateId}</code></div>` : ''}
            ${view.judge.unclearPart ? html`<div>Uncertain email part: ${view.judge.unclearPart}</div>` : ''}
            ${view.judge.reason ? html`<p class="mt-1">Verifier explanation: ${view.judge.reason}</p>` : ''}
          </section>`
        : ''}
      <section class="border-t border-base-300 pt-2">
        <strong>Server decision: ${view.decision.outcome}</strong>
        ${view.decision.reason ? html`<p>${view.decision.reason}</p>` : ''}
        ${view.decision.selectedCandidateId
          ? html`<div>Selected contact: <code>${view.decision.selectedCandidateId}</code></div>`
          : ''}
        ${view.decision.nextPhase ? html`<div>Next step: ${view.decision.nextPhase}</div>` : ''}
        ${view.elapsedMs === undefined ? '' : html`<div>Verification duration: ${view.elapsedMs} ms</div>`}
      </section>
    </div>
  </details>`;
};

export const renderCallDiagnostic = (log: CallDiagnosticLog) => {
  const data = diagnosticPayload(log);
  if (log.type === 'authentication-check') return renderAuthenticationDiagnostic(data, diagnosticSummary(log));
  return html`<details class="rounded-lg border border-info/25 bg-info/5 p-2.5 text-xs my-1">
    <summary class="cursor-pointer wrap-break-word font-medium">${diagnosticSummary(log)}</summary>
    <div class="mt-2 text-base-content/65 wrap-break-word">
      ${data.requestID ? html`<div>Anfrage: <code>${data.requestID}</code></div>` : ''}
      ${data.generationID ? html`<div>Antwortgenerierung: <code>${data.generationID}</code></div>` : ''}
      ${data.elapsedMs != null ? html`<div>Dauer: ${data.elapsedMs} ms</div>` : ''}
    </div>
    <pre class="mt-2 whitespace-pre-wrap wrap-break-word max-h-96 overflow-auto text-[11px]">
${JSON.stringify(data, null, 2)}</pre
    >
  </details>`;
};

export const renderCallLatency = (logs: CallDiagnosticLog[]) => {
  const values = measureCallLatency(logs);
  const ms = (value: number | null) => (value == null ? 'Nicht erfasst' : `${Math.round(value)} ms`);
  return html`<details class="rounded-lg border border-base-300 p-3 mb-3 text-sm">
    <summary class="cursor-pointer font-semibold">Antwortzeiten aus vorhandenen Protokollen</summary>
    <dl class="grid grid-cols-2 gap-2 mt-3">
      <dt>STT: Sprachende → erste Transkription</dt>
      <dd>${ms(values.sttAfterSpeechMs)}</dd>
      <dt>LLM: erstes Token</dt>
      <dd>${ms(values.llmFirstTokenMs)}</dd>
      <dt>TTS: erster Audio-Chunk</dt>
      <dd>${ms(values.ttsFirstChunkMs)}</dd>
      <dt>Sprachende → erster Audio-Chunk</dt>
      <dd>${ms(values.speechToFirstAudioMs)}</dd>
      <dt>CRM: belegte Zeit (${values.crmRequestsMeasured} Anfragen)</dt>
      <dd>${ms(values.crmBusyMs)}</dd>
    </dl>
    <p class="mt-2 text-base-content/60">
      Jeweils letzte messbare Sprach-/Modellprobe. CRM über die angezeigten Protokolle; parallele Anfragen zählen nicht
      doppelt. Zeiten überlappen und werden nicht addiert. Audio-Chunk bedeutet serverseitige Bereitstellung, nicht
      bestätigte Wiedergabe beim Anrufer.
    </p>
  </details>`;
};
